import React, { type ComponentProps, type RefObject, useCallback, useEffect, useRef } from 'react';
import {
  type LayoutChangeEvent,
  type NativeMethods,
  type ScrollView,
  StyleSheet,
  TouchableOpacity,
  type TouchableOpacityProps,
} from 'react-native';

import { theme } from '@garnish/constants';

import { Button } from '../../../Button';

// ─────────────────────────────────────────────────────────────────────────────

export const ScrollspyNavItem = (props: ScrollspyNavItemProps) => {
  const { children, isActive = false, onLayout, ...buttonProps } = props;

  const { ref, handleOnPress, handleLayout } = useScrollspyNavItem(props);

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleLayoutWithForward = useCallback(
    (event: LayoutChangeEvent) => {
      handleLayout(event);

      if (typeof onLayout === 'function') {
        onLayout(event);
      }
    },
    [handleLayout, onLayout],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Button
      {...buttonProps}
      ref={ref}
      size="large"
      palette={isActive ? 'primary' : 'secondary'}
      style={styles.pressable}
      accessibilityState={{ selected: isActive }}
      onPress={handleOnPress}
      onLayout={handleLayoutWithForward}
    >
      {children}
    </Button>
  );
};

/**
 * Minimal pressable wrapper for custom nav items. Handles targetId/onPress
 * but lets the consumer render arbitrary content.
 */
export const ScrollspyNavItemPressable = (props: ScrollspyNavItemPressableProps) => {
  const { children, isActive = false, onLayout, ...pressableProps } = props;

  const { ref, handleOnPress, handleLayout } = useScrollspyNavItem(props);

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleLayoutWithForward = useCallback(
    (event: LayoutChangeEvent) => {
      handleLayout(event);

      if (typeof onLayout === 'function') {
        onLayout(event);
      }
    },
    [handleLayout, onLayout],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      {...pressableProps}
      ref={ref}
      accessibilityState={{ selected: isActive }}
      onPress={handleOnPress}
      onLayout={handleLayoutWithForward}
    >
      {children}
    </TouchableOpacity>
  );
};

// ─── Hook ────────────────────────────────────────────────────────────────────

const useScrollspyNavItem = (params: ScrollspyNavItemParams) => {
  const { targetId, isActive = false, navScrollViewRef, onPress } = params;

  // ─── Refs ────────────────────────────────────────────────────────────────────

  const ref = useRef(null);
  const layoutRef = useRef({ x: 0, width: 0 });

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.(targetId);
  }, [onPress, targetId]);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { x, width } = event.nativeEvent.layout;

    layoutRef.current = { x, width };
  }, []);

  const improveScrollPositionIfNeeded = useCallback(() => {
    const scrollView = navScrollViewRef?.current;

    if (!isActive || !scrollView) return;

    const itemNode = ref.current as NativeMethods | null;

    if (!itemNode) return;

    itemNode.measure((_ix, _iy, itemWidth, _ih, itemPageX) => {
      (scrollView as unknown as NativeMethods).measure((_nx, _ny, navWidth, _nh, navPageX) => {
        const viewportX = itemPageX - navPageX;
        const isComfortablyVisible =
          viewportX >= EDGE_PADDING && viewportX + itemWidth <= navWidth - EDGE_PADDING;

        if (isComfortablyVisible) return;

        const { x: contentX } = layoutRef.current;

        scrollView.scrollTo({ x: Math.max(0, contentX - EDGE_PADDING), animated: true });
      });
    });
  }, [navScrollViewRef, isActive]);

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(improveScrollPositionIfNeeded, [improveScrollPositionIfNeeded]);

  // ─────────────────────────────────────────────────────────────────────────────

  return { ref, handleOnPress, handleLayout };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const EDGE_PADDING = 48;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    height: 32,
    paddingHorizontal: theme.spacing['3'],
    borderWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScrollspyNavItemParams = {
  targetId: string;
  isActive?: boolean;
  navScrollViewRef?: RefObject<ScrollView | null>;
  onPress?: (targetId: string) => void;
};

type ScrollspyNavItemProps = ScrollspyNavItemParams &
  Omit<ComponentProps<typeof Button>, 'palette' | 'onPress'>;

type ScrollspyNavItemPressableProps = ScrollspyNavItemParams &
  Omit<TouchableOpacityProps, 'onPress'>;
